// Shared line-item categories for Operational Requests only.
// Standard Cash / Check Vouchers hide the category column.

export const BUDGET_ITEM_CATEGORIES = [
  { value: "transpo_travel", label: "Transpo/Travel" },
  { value: "purchases", label: "Purchases" },
  { value: "representation", label: "Representation" },
  { value: "contingency_fund", label: "Contingency Fund" },
  { value: "technical_services", label: "Technical Services Expense" },
  { value: "other", label: "Other" },
];

export const BUDGET_ITEM_SUBCATEGORIES = {
  technical_services: [
    { value: "hotel", label: "Hotel" },
    { value: "charge", label: "Charge" },
    { value: "allowances", label: "Allowances" },
  ],
};

export const CATEGORY_LABEL = Object.fromEntries(
  BUDGET_ITEM_CATEGORIES.map((c) => [c.value, c.label])
);

export const SUBCATEGORY_LABEL = Object.fromEntries(
  Object.values(BUDGET_ITEM_SUBCATEGORIES)
    .flat()
    .map((s) => [s.value, s.label])
);

export function formatCategoryFull(item) {
  if (!item?.category) return "";
  const cat = CATEGORY_LABEL[item.category] || item.category;
  if (item.sub_category && BUDGET_ITEM_SUBCATEGORIES[item.category]) {
    const sub = SUBCATEGORY_LABEL[item.sub_category] || item.sub_category;
    return `${cat} — ${sub}`;
  }
  return cat;
}

// Cash-Voucher-only accounting categories (voucher-level, optional).
export const CASH_VOUCHER_CATEGORIES = [
  { value: "advances_to_affiliate", label: "Advances to Affiliate" },
  { value: "advances_to_officers", label: "Advances to Officers" },
  { value: "tools_and_equipment", label: "Tools and Equipment" },
  { value: "cebu_expenses", label: "Cebu Expenses" },
  { value: "shipment_expenses", label: "Shipment Expenses" },
  { value: "light_and_water", label: "Light & Water" },
  { value: "loan_payable", label: "Loan Payable" },
  { value: "tele_telex", label: "Tele/Telex" },
  { value: "salary", label: "Salary" },
  { value: "personal_ca", label: "Personal CA" },
  { value: "purchases", label: "Purchases" },
  { value: "repairs_and_maintenance", label: "Repairs and Maintenance" },
  { value: "representation", label: "Representation" },
  { value: "professional_fee", label: "Professional Fee" },
  { value: "training_seminar", label: "Training/ Seminar" },
  { value: "commission", label: "Commission" },
  { value: "rental", label: "Rental" },
  { value: "fuel", label: "Fuel" },
  { value: "sss_pay", label: "SSS Pay" },
  { value: "pag_ibig_fee", label: "Pag-ibig Fee" },
  { value: "philhealth_pay", label: "Philhealth Pay" },
  { value: "taxes_and_licenses", label: "Taxes and Licenses" },
  { value: "management_expenses", label: "Management Expenses" },
  { value: "construction_expenses", label: "Construction Expenses" },
  { value: "input_tax", label: "Input Tax" },
  { value: "employee_welfare", label: "Employee's Welfare" },
  { value: "allowance", label: "Allowance" },
  { value: "accounts_payable", label: "Accounts Payable" },
];

export const CASH_VOUCHER_CATEGORY_LABEL = Object.fromEntries(
  CASH_VOUCHER_CATEGORIES.map((c) => [c.value, c.label])
);

// Fixed row layout for the Operational Request category editor. Each row is
// an { category, sub_category?, label } bucket that the editor renders in a
// stable order. The last row (Other) accepts a custom description.
export const OPERATIONAL_CATEGORY_ROWS = [
  { key: "transpo_travel", category: "transpo_travel", label: "Transpo/Travel" },
  { key: "purchases", category: "purchases", label: "Purchases" },
  { key: "representation", category: "representation", label: "Representation" },
  { key: "contingency_fund", category: "contingency_fund", label: "Contingency Fund" },
  { key: "technical_services:hotel", category: "technical_services", sub_category: "hotel", label: "Technical Services — Hotel" },
  { key: "technical_services:charge", category: "technical_services", sub_category: "charge", label: "Technical Services — Charge" },
  { key: "technical_services:allowances", category: "technical_services", sub_category: "allowances", label: "Technical Services — Allowances" },
  { key: "other", category: "other", label: "Other" },
];

// Convert a list of stored line items into an amounts-by-row map.
// Unmatched items are folded into the "Other" bucket so their money never
// disappears when re-opening a legacy request.
export function itemsToCategoryMap(items) {
  const map = Object.fromEntries(
    OPERATIONAL_CATEGORY_ROWS.map((r) => [r.key, { amount: 0, description: "" }])
  );
  for (const it of items || []) {
    const amt = Number(it?.amount) || (Number(it?.quantity) || 0) * (Number(it?.unit_price) || 0);
    if (!amt) continue;
    const cat = it?.category;
    const sub = it?.sub_category;
    const row = OPERATIONAL_CATEGORY_ROWS.find((r) =>
      r.category === cat && (r.sub_category || null) === (sub || null)
    );
    const key = row ? row.key : "other";
    map[key].amount += amt;
    // Preserve the first non-empty description for the Other row so custom
    // labels survive edits.
    if (key === "other" && !map.other.description && it?.description) {
      map.other.description = it.description;
    }
  }
  return map;
}

// Convert an amounts-by-row map back into line items ready for the API.
// Skips zero-amount rows so submitted documents stay tidy.
export function categoryMapToItems(map) {
  const out = [];
  for (const row of OPERATIONAL_CATEGORY_ROWS) {
    const cell = map[row.key];
    const amount = Number(cell?.amount) || 0;
    if (!amount) continue;
    const description = row.key === "other" && cell?.description
      ? cell.description
      : row.label;
    out.push({
      description,
      quantity: 1,
      unit_price: amount,
      category: row.category,
      sub_category: row.sub_category || null,
    });
  }
  return out;
}
